/**
 * 분포도 과거 보기의 표본 뽑기 (23-A회차 리뷰 2).
 *
 * 검색 API는 최신순이라 `limit=5000` 한 번으로는 **가장 최근 5,000건**만 온다. 5,000건이 넘는
 * 기간을 고르면 앞 시간대가 통째로 비어 보이고, 그림으로 밀도·이상 구간을 읽을 수 없다.
 * 그래서 기간을 같은 폭의 조각으로 나눠 조각마다 몫만큼 받는다. 시간대별로 고르게 뽑히고,
 * 같은 기간을 다시 열면 같은 조회가 나가므로 **결정적**이다.
 *
 * 조각 안에서는 여전히 최신순이다. 조각 하나가 몫보다 붐비면 그 조각의 뒤쪽이 촘촘해진다 —
 * 조각을 잘게 나눌수록 덜하지만 조회 수가 그만큼 늘어난다. 열둘은 1시간이면 5분, 하루면 2시간
 * 단위다.
 *
 * 여기 있는 것은 순수 함수다. 부르는 쪽(`usePastRows`)이 조회와 취소를 맡는다.
 */

/**
 * 한 번에 그리는 최대 점 수. 서버 조회 상한(`APM_QUERY_MAX_LIMIT`, 기본 5,000)과 같은 값이다.
 * 서버 상한이 더 낮게 설정돼 있으면 실제로 받는 수가 이보다 적고, 화면은 **받은 수**를 적는다
 */
export const PAST_LIMIT = 5_000;

/** 기간을 나누는 조각 수 */
export const PAST_SLICES = 12;

export interface Slice {
  from: number;
  to: number;
}

/**
 * 기간을 같은 폭의 조각으로. 조각은 겹치지 않고 빈틈도 없다(`to`는 포함).
 * 폭이 조각 수보다 좁으면 나누지 않는다 — 1ms짜리 조각을 열둘 만들 이유가 없다
 */
export function sliceRange(from: number, to: number, slices: number = PAST_SLICES): Slice[] {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [{ from, to }];
  const count = Math.max(1, Math.min(slices, Math.floor(to - from)));
  if (count === 1) return [{ from, to }];
  const width = (to - from) / count;
  const out: Slice[] = [];
  for (let index = 0; index < count; index += 1) {
    const start = index === 0 ? from : Math.round(from + width * index);
    // 마지막 조각만 끝을 포함한다. 나머지는 다음 조각 시작 직전까지다
    const end = index === count - 1 ? to : Math.round(from + width * (index + 1)) - 1;
    if (end >= start) out.push({ from: start, to: end });
  }
  return out;
}

/** 조각 하나가 받아 올 수. 합이 상한을 넘지 않게 올림 대신 내림으로 잡고 최소 1은 준다 */
export function sliceLimit(limit: number = PAST_LIMIT, slices: number = PAST_SLICES): number {
  return Math.max(1, Math.floor(limit / Math.max(1, slices)));
}
